import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Container } from "reactstrap";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";

import { Heading } from "components/Heading";
import { PageHeadings, PageTitles } from "../../../constants";
import typography from "../typography";
import Header from "./Header";
import DataTable from "../../../components/DataTable/DataTable";
import { truncateString } from "../../../utils/commonUtils";
import viewIcon from "../../../assets/images/app/common/aou-view-icon.svg";
import editIcon from "../../../assets/images/app/common/pencil-icon.svg";
import deleteIcon from "../../../assets/images/app/common/trash-icon.svg";
import { Pagination } from "../../../components/Pagination";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { ConfirmationModal } from "../../../components/CustomModal";
import {
    getAudioVideoContents,
    deleteAudioVideoContent,
    restoreAudioVideoContent,
    permanentlyDeleteAudioVideoContent,
} from "../../../api/api.service";
import { useLocalPaginatedRequest } from "../../../hooks/useLocalPaginatedRequest";
import { formatDuration } from "../utils";

// Two-step delete: the trash icon in the Active tab soft-deletes (moves to
// the Bin tab, see Header's status filter) — reversible, no destructive
// consequence, so it still gets a confirm dialog but a mild one. Only the
// Bin tab's "Delete Forever" is the actual point of no return, and gets its
// own separate, more serious confirmation rather than reusing the same
// dialog with different copy swapped in — the two actions have different
// stakes and shouldn't share one piece of state that has to remember which
// kind of delete is in flight.
const Listing = () => {
    const history = useHistory();

    const [filters, setFilters] = useState({ contentType: "ALL", stateKey: "ALL", status: "ACTIVE" });

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null);
    const [isPermanentlyDeleting, setIsPermanentlyDeleting] = useState(false);
    const [restoringId, setRestoringId] = useState(null);

    const {
        isFetching,
        page,
        total,
        data,
        searchText,
        request: refetch,
        handlePageClick,
        onChangePageSize,
        pageSize,
        handleSearchChange,
        setPageToOne,
    } = useLocalPaginatedRequest(
        getAudioVideoContents,
        {
            ...(filters.contentType !== "ALL" && { contentType: filters.contentType }),
            ...(filters.stateKey !== "ALL" && { stateKey: filters.stateKey }),
            ...(filters.status !== "ACTIVE" && { status: filters.status }),
        },
        [filters.contentType, filters.stateKey, filters.status],
        "Q"
    );

    const isBinView = filters.status === "BIN";

    const handleDetailsView = (id) => {
        history.push(`${APP_ROUTES.CONTENT_LIBRARY_DETAILS}/${id}`);
    };

    const handleEditContent = (id) => {
        history.push(`${APP_ROUTES.EDIT_CONTENT_LIBRARY}/${id}`);
    };

    const handleAddContent = () => {
        const query = filters.stateKey !== "ALL" ? `?state=${filters.stateKey}` : "";
        history.push(`${APP_ROUTES.ADD_CONTENT_LIBRARY}${query}`);
    };

    const handleManageLockedSessions = () => {
        history.push(APP_ROUTES.LOCKED_SESSION_CONTENT);
    };

    const handleFilterChange = (value, type) => {
        setPageToOne();
        setFilters({ ...filters, [type]: value });
    };

    const confirmSoftDelete = async () => {
        if (!deleteTarget?.id) {
            setDeleteTarget(null);
            return;
        }
        try {
            setIsDeleting(true);
            const response = await deleteAudioVideoContent(deleteTarget.id);
            toast.success(response?.message || "Moved to the bin");
            refetch();
        } catch (error) {
            // handleFetchError already toasts the backend's message.
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };

    const handleRestore = async (row) => {
        // Restoring is reversible (it can always be deleted again) and low
        // stakes compared to the two delete actions, so it skips a confirm
        // dialog — just does it, with its own per-row loading state so a
        // double-click can't fire it twice.
        setRestoringId(row.id);
        try {
            const response = await restoreAudioVideoContent(row.id);
            toast.success(response?.message || "Restored");
            refetch();
        } catch (error) {
            // handleFetchError already toasts the backend's message —
            // including the "another item now has this slot" rejection.
        } finally {
            setRestoringId(null);
        }
    };

    const confirmPermanentDelete = async () => {
        if (!permanentDeleteTarget?.id) {
            setPermanentDeleteTarget(null);
            return;
        }
        try {
            setIsPermanentlyDeleting(true);
            const response = await permanentlyDeleteAudioVideoContent(permanentDeleteTarget.id);
            toast.success(response?.message || "Permanently deleted");
            refetch();
        } catch (error) {
            // handleFetchError already toasts the backend's message —
            // including the "has real play/favorite history" rejection,
            // which is the expected outcome for content that's actually
            // been used, not a bug to retry around.
        } finally {
            setIsPermanentlyDeleting(false);
            setPermanentDeleteTarget(null);
        }
    };

    return (
        <div className="page-content">
            <Helmet>
                <title>{PageTitles.CONTENT_LIBRARY}</title>
            </Helmet>
            <Container fluid className="bls-feedback customer-management">
                <Heading heading={PageHeadings.CONTENT_LIBRARY} />
                <Header
                    contentTypeFilterVal={filters.contentType}
                    stateKeyFilterVal={filters.stateKey}
                    statusFilterVal={filters.status}
                    filterUpdateHandler={handleFilterChange}
                    searchTextVal={searchText}
                    searchValUpdateHandler={handleSearchChange}
                    onAddContent={handleAddContent}
                    onManageLockedSessions={handleManageLockedSessions}
                />
                <DataTable
                    data={data}
                    loading={isFetching}
                    config={[
                        {
                            title: "",
                            render: (row) => (
                                <div className="content-thumb-cell">
                                    {row?.thumbnailUrl ? (
                                        // eslint-disable-next-line jsx-a11y/alt-text
                                        <img src={row.thumbnailUrl} alt="" className="content-thumb-image" />
                                    ) : (
                                        <div className={`content-thumb-placeholder content-type-${(row?.contentType || "").toLowerCase()}`} />
                                    )}
                                </div>
                            ),
                        },
                        {
                            title: typography.TITLE,
                            render: (row) => (
                                <span className="ms-2 standard-typo-14-400">
                                    {truncateString(row?.title, 40) || "-"}
                                </span>
                            ),
                        },
                        {
                            title: typography.AUTHOR,
                            render: (row) => (
                                <div className="standard-typo-14-400">{row?.author || "-"}</div>
                            ),
                        },
                        {
                            title: typography.STATE,
                            render: (row) => (
                                <div className="standard-typo-14-400 text-capitalize">{row?.stateKey || "-"}</div>
                            ),
                        },
                        {
                            title: typography.CONTENT_TYPE,
                            render: (row) => (
                                <div
                                    className={`content-type-badge ${
                                        row?.contentType === "AUDIO" ? "content-type-audio" : "content-type-video"
                                    }`}
                                >
                                    {row?.contentType === "AUDIO" ? typography.AUDIO : typography.VIDEO}
                                </div>
                            ),
                        },
                        {
                            title: typography.DURATION,
                            render: (row) => (
                                <div className="standard-typo-14-400">{formatDuration(row?.duration)}</div>
                            ),
                        },
                        {
                            title: typography.ACTION,
                            render: (row) =>
                                isBinView ? (
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 standard-typo-14-600"
                                            disabled={restoringId === row.id}
                                            onClick={() => handleRestore(row)}
                                        >
                                            {restoringId === row.id ? "Restoring…" : typography.RESTORE}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 standard-typo-14-600 text-danger"
                                            onClick={() => setPermanentDeleteTarget(row)}
                                        >
                                            {typography.PERMANENTLY_DELETE}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="d-flex">
                                        <div
                                            className="view-form-icon-con"
                                            onClick={() => handleDetailsView(row.id)}
                                        >
                                            <img src={viewIcon} alt="view icon" role="button" />
                                        </div>
                                        <div
                                            className="view-form-icon-con ms-2"
                                            onClick={() => handleEditContent(row.id)}
                                        >
                                            <img src={editIcon} alt="edit icon" role="button" />
                                        </div>
                                        <div
                                            className="view-form-icon-con ms-2"
                                            onClick={() => setDeleteTarget(row)}
                                        >
                                            <img src={deleteIcon} alt="delete icon" role="button" />
                                        </div>
                                    </div>
                                ),
                        },
                    ]}
                />
                <Pagination
                    onPageClick={handlePageClick}
                    currentPage={page}
                    totalData={total}
                    handleChange={(e) => onChangePageSize(e)}
                    rowLimit={pageSize}
                />
            </Container>

            <ConfirmationModal
                isOpen={!!deleteTarget}
                hasCustomConfig={true}
                heading={typography.DELETE_CONFIRM_HEADING}
                onNo={() => setDeleteTarget(null)}
                onYes={confirmSoftDelete}
                customYesBtnText={isDeleting ? typography.DELETING : typography.DELETE_CONFIRM_YES}
                customNoBtnText="Cancel"
                customImageSrc={deleteIcon}
                disabled={isDeleting}
            />

            <ConfirmationModal
                isOpen={!!permanentDeleteTarget}
                hasCustomConfig={true}
                heading={typography.PERMANENT_DELETE_CONFIRM_HEADING}
                onNo={() => setPermanentDeleteTarget(null)}
                onYes={confirmPermanentDelete}
                customYesBtnText={isPermanentlyDeleting ? typography.DELETING : typography.PERMANENT_DELETE_CONFIRM_YES}
                customNoBtnText="Cancel"
                customImageSrc={deleteIcon}
                disabled={isPermanentlyDeleting}
            />
        </div>
    );
};

export default Listing;
